import { roundTo } from '../../../shared/chart-shared';
import type { ScatterPlotPoint } from './scatter-plot-chart.model';

// ---------------------------------------------------------------------------
// Numeric helpers
// ---------------------------------------------------------------------------

/** Clamp exponent to prevent Math.exp overflow (e^709 ≈ 8.2e307, e^710 = Infinity). */
function safeExp(x: number): number {
  return Math.exp(Math.max(-700, Math.min(700, x)));
}

/** Compute min and max in a single pass without spreading onto the call stack. */
export function minMax(values: readonly number[]): { min: number; max: number } {
  let min = values[0];
  let max = values[0];
  for (let i = 1; i < values.length; i++) {
    const v = values[i];
    if (v < min) {
      min = v;
    }
    if (v > max) {
      max = v;
    }
  }
  return { min, max };
}

// ---------------------------------------------------------------------------
// Ranking helpers (for Spearman)
// ---------------------------------------------------------------------------

function rankValues(values: number[]): number[] {
  const indexed = values.map((v, i) => ({ value: v, index: i }));
  indexed.sort((a, b) => a.value - b.value);

  const ranks = new Array<number>(values.length);
  let i = 0;
  while (i < indexed.length) {
    let j = i;
    while (j < indexed.length && indexed[j].value === indexed[i].value) {
      j++;
    }
    const avgRank = (i + 1 + j) / 2;
    for (let k = i; k < j; k++) {
      ranks[indexed[k].index] = avgRank;
    }
    i = j;
  }
  return ranks;
}

// ---------------------------------------------------------------------------
// Spearman's rank correlation
// ---------------------------------------------------------------------------

/**
 * Ranks x and y independently (average ranks for ties),
 * then computes PMCC on the ranks.
 */
export function computeSpearman(points: ScatterPlotPoint[]): number | null {
  if (points.length < 2) {
    return null;
  }

  const xRanks = rankValues(points.map((p) => p.x));
  const yRanks = rankValues(points.map((p) => p.y));

  const rankedPoints: ScatterPlotPoint[] = xRanks.map((xr, i) => ({ x: xr, y: yRanks[i] }));
  const result = computeRegression(rankedPoints);
  return result ? roundTo(result.r, 6) : null;
}

// ---------------------------------------------------------------------------
// Linear regression (least squares)
// ---------------------------------------------------------------------------

export interface RegressionResult {
  /** Slope */
  readonly a: number;
  /** Intercept */
  readonly b: number;
  /** Pearson correlation coefficient */
  readonly r: number;
  /** Coefficient of determination */
  readonly rSquared: number;
}

/**
 * Compute least-squares linear regression y = ax + b and Pearson r.
 * Returns null if fewer than 2 points or zero variance in x.
 */
export function computeRegression(points: ScatterPlotPoint[]): RegressionResult | null {
  const n = points.length;
  if (n < 2) {
    return null;
  }

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;

  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
    sumXY += p.x * p.y;
    sumX2 += p.x * p.x;
    sumY2 += p.y * p.y;
  }

  const denomX = n * sumX2 - sumX * sumX;
  const denomY = n * sumY2 - sumY * sumY;

  if (denomX === 0) {
    return null;
  }

  const a = (n * sumXY - sumX * sumY) / denomX;
  const b = (sumY - a * sumX) / n;

  // Pearson r
  const denomR = Math.sqrt(denomX * denomY);
  const r = denomR === 0 ? 0 : (n * sumXY - sumX * sumY) / denomR;

  return {
    a: roundTo(a, 6),
    b: roundTo(b, 6),
    r: roundTo(r, 6),
    rSquared: roundTo(r * r, 6),
  };
}

/**
 * Predict y for a given x using regression line y = ax + b.
 */
export function predictY(regression: RegressionResult, x: number): number {
  return regression.a * x + regression.b;
}

// ---------------------------------------------------------------------------
// Inverse regression (x on y)
// ---------------------------------------------------------------------------

export interface InverseRegressionResult {
  /** Intercept (value of x when y = 0) */
  readonly c: number;
  /** Slope (change in x per unit change in y) */
  readonly d: number;
}

/**
 * Compute least-squares inverse regression x = c + dy.
 * Reuses computeRegression with x and y swapped.
 * Returns null if fewer than 2 points or zero variance in y.
 */
export function computeInverseRegression(points: ScatterPlotPoint[]): InverseRegressionResult | null {
  const swapped = points.map((p) => ({ x: p.y, y: p.x }));
  const result = computeRegression(swapped);
  if (!result) {
    return null;
  }
  return { c: result.b, d: result.a };
}

/**
 * Predict x for a given y using inverse regression line x = c + dy.
 */
export function predictX(inverse: InverseRegressionResult, y: number): number {
  return inverse.c + inverse.d * y;
}

/**
 * Compute residual line endpoints for each data point.
 * Returns an array of [x, actual_y, predicted_y] tuples.
 */
export function computeResiduals(
  points: ScatterPlotPoint[],
  regression: RegressionResult,
): Array<{ x: number; actual: number; predicted: number }> {
  return points.map((p) => ({
    x: p.x,
    actual: p.y,
    predicted: predictY(regression, p.x),
  }));
}

// ---------------------------------------------------------------------------
// Quadratic regression (least squares: y = ax² + bx + c)
// ---------------------------------------------------------------------------

export interface QuadraticRegressionResult {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly rSquared: number;
}

/**
 * Compute least-squares quadratic regression y = ax² + bx + c.
 * Uses the normal equations for polynomial regression of degree 2.
 * Centers x values around the mean for numerical stability.
 */
export function computeQuadraticRegression(points: ScatterPlotPoint[]): QuadraticRegressionResult | null {
  const n = points.length;
  if (n < 3) {
    return null;
  }

  // Center x values around mean for numerical stability
  let xSum = 0;
  for (const p of points) {
    xSum += p.x;
  }
  const xMean = xSum / n;

  let sx = 0,
    sx2 = 0,
    sx3 = 0,
    sx4 = 0;
  let sy = 0,
    sxy = 0,
    sx2y = 0;

  for (const p of points) {
    const xc = p.x - xMean;
    const xc2 = xc * xc;
    sx += xc;
    sx2 += xc2;
    sx3 += xc2 * xc;
    sx4 += xc2 * xc2;
    sy += p.y;
    sxy += xc * p.y;
    sx2y += xc2 * p.y;
  }

  // Solve 3x3 system using Cramer's rule
  // | sx4  sx3  sx2 | | a' |   | sx2y |
  // | sx3  sx2  sx  | | b' | = | sxy  |
  // | sx2  sx   n   | | c' |   | sy   |

  const det = sx4 * (sx2 * n - sx * sx) - sx3 * (sx3 * n - sx * sx2) + sx2 * (sx3 * sx - sx2 * sx2);

  if (Math.abs(det) < 1e-10) {
    return null;
  }

  const ac = (sx2y * (sx2 * n - sx * sx) - sx3 * (sxy * n - sy * sx) + sx2 * (sxy * sx - sy * sx2)) / det;

  const bc = (sx4 * (sxy * n - sy * sx) - sx2y * (sx3 * n - sx * sx2) + sx2 * (sx3 * sy - sx2 * sxy)) / det;

  const cc = (sx4 * (sx2 * sy - sx * sxy) - sx3 * (sx3 * sy - sx * sx2y) + sx2 * (sx3 * sxy - sx2 * sx2y)) / det;

  // Convert centered coefficients back: y = ac*(x-xMean)² + bc*(x-xMean) + cc
  const a = ac;
  const b = bc - 2 * ac * xMean;
  const c = ac * xMean * xMean - bc * xMean + cc;

  // R² calculation
  const meanY = sy / n;
  let ssTot = 0,
    ssRes = 0;
  for (const p of points) {
    const predicted = a * p.x * p.x + b * p.x + c;
    ssTot += (p.y - meanY) ** 2;
    ssRes += (p.y - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    a: roundTo(a, 6),
    b: roundTo(b, 6),
    c: roundTo(c, 6),
    rSquared: roundTo(rSquared, 6),
  };
}

/**
 * Evaluate quadratic regression at a given x.
 */
export function predictQuadraticY(reg: QuadraticRegressionResult, x: number): number {
  return reg.a * x * x + reg.b * x + reg.c;
}

// ---------------------------------------------------------------------------
// Sinusoidal regression (y = a·sin(bx + c) + d)
// ---------------------------------------------------------------------------

export interface SineRegressionResult {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly rSquared: number;
}

/**
 * Compute a sinusoidal fit y = a·sin(bx + c) + d using an iterative approach.
 * This is a simplified estimator:
 * - d = mean(y)
 * - a = (max(y) - min(y)) / 2
 * - b estimated from dominant frequency via zero-crossings
 * - c estimated from first peak position
 * Then we refine with gradient descent iterations.
 */
export function computeSineRegression(points: ScatterPlotPoint[]): SineRegressionResult | null {
  const n = points.length;
  if (n < 4) {
    return null;
  }

  const sorted = [...points].sort((a, b) => a.x - b.x);

  let yMin = sorted[0].y;
  let yMax = sorted[0].y;
  let ySum = 0;
  for (const p of sorted) {
    if (p.y < yMin) {
      yMin = p.y;
    }
    if (p.y > yMax) {
      yMax = p.y;
    }
    ySum += p.y;
  }
  const yMean = ySum / n;

  // Initial estimates
  let d = yMean;
  let a = (yMax - yMin) / 2;

  // Estimate period from data range
  const xRange = sorted[n - 1].x - sorted[0].x;
  // Try to detect period by counting sign changes in (y - mean)
  let crossings = 0;
  for (let i = 1; i < n; i++) {
    if ((sorted[i].y - d) * (sorted[i - 1].y - d) < 0) {
      crossings++;
    }
  }
  const halfPeriods = Math.max(crossings, 1);
  const period = (2 * xRange) / halfPeriods;
  let b = (2 * Math.PI) / period;

  // Estimate phase shift c from first peak
  let maxIdx = 0;
  for (let i = 1; i < n; i++) {
    if (sorted[i].y > sorted[maxIdx].y) {
      maxIdx = i;
    }
  }
  let c = Math.PI / 2 - b * sorted[maxIdx].x;

  // Scale learning rate relative to data magnitude
  const yScale = Math.max(yMax - yMin, 1);
  const xScale = Math.max(xRange, 1);
  const baseLr = 0.0001;

  for (let iter = 0; iter < 500; iter++) {
    let da = 0,
      db = 0,
      dc = 0,
      dd = 0;
    for (const p of sorted) {
      const sinVal = Math.sin(b * p.x + c);
      const cosVal = Math.cos(b * p.x + c);
      const residual = a * sinVal + d - p.y;
      da += residual * sinVal;
      db += residual * a * cosVal * p.x;
      dc += residual * a * cosVal;
      dd += residual;
    }
    a -= baseLr * yScale * (2 / n) * da;
    b -= (baseLr / xScale) * (2 / n) * db;
    c -= baseLr * (2 / n) * dc;
    d -= baseLr * yScale * (2 / n) * dd;
  }

  // Ensure positive amplitude
  if (a < 0) {
    a = -a;
    c += Math.PI;
  }

  // R² calculation
  let ssTot = 0,
    ssRes = 0;
  for (const p of sorted) {
    const predicted = a * Math.sin(b * p.x + c) + d;
    ssTot += (p.y - yMean) ** 2;
    ssRes += (p.y - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    a: roundTo(a, 4),
    b: roundTo(b, 4),
    c: roundTo(c, 4),
    d: roundTo(d, 4),
    rSquared: roundTo(rSquared, 6),
  };
}

/**
 * Evaluate sine regression at a given x.
 */
export function predictSineY(reg: SineRegressionResult, x: number): number {
  return reg.a * Math.sin(reg.b * x + reg.c) + reg.d;
}

// ---------------------------------------------------------------------------
// Gaussian elimination (shared helper for polynomial regression)
// ---------------------------------------------------------------------------

/**
 * Solve NxN linear system Ax = b using Gaussian elimination with partial pivoting.
 * Returns null if the system is singular.
 */
function solveLinearSystem(A: number[][], b: number[]): number[] | null {
  const n = A.length;
  const aug = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let maxRow = col;
    let maxVal = Math.abs(aug[col][col]);
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(aug[row][col]) > maxVal) {
        maxVal = Math.abs(aug[row][col]);
        maxRow = row;
      }
    }
    if (maxVal < 1e-12) {
      return null;
    }
    if (maxRow !== col) {
      [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
    }

    for (let row = col + 1; row < n; row++) {
      const factor = aug[row][col] / aug[col][col];
      for (let j = col; j <= n; j++) {
        aug[row][j] -= factor * aug[col][j];
      }
    }
  }

  const x = new Array<number>(n);
  for (let i = n - 1; i >= 0; i--) {
    let sum = aug[i][n];
    for (let j = i + 1; j < n; j++) {
      sum -= aug[i][j] * x[j];
    }
    x[i] = sum / aug[i][i];
  }

  return x;
}

// ---------------------------------------------------------------------------
// Cubic regression (least squares: y = ax³ + bx² + cx + d)
// ---------------------------------------------------------------------------

export interface CubicRegressionResult {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly rSquared: number;
}

/**
 * Compute least-squares cubic regression y = ax³ + bx² + cx + d.
 * Centers x values around the mean for numerical stability.
 */
export function computeCubicRegression(points: ScatterPlotPoint[]): CubicRegressionResult | null {
  const n = points.length;
  if (n < 4) {
    return null;
  }

  let xSum = 0;
  let ySum = 0;
  for (const p of points) {
    xSum += p.x;
    ySum += p.y;
  }
  const xMean = xSum / n;
  const yMean = ySum / n;

  // Build normal equations in centered coordinates
  const sums = new Array(7).fill(0); // sx^0..sx^6
  sums[0] = n;
  const rhs = new Array(4).fill(0); // sx^k * y for k=0..3
  rhs[0] = ySum;

  for (const p of points) {
    const xc = p.x - xMean;
    let xpow = 1;
    for (let k = 1; k <= 6; k++) {
      xpow *= xc;
      sums[k] += xpow;
    }
    rhs[1] += xc * p.y;
    rhs[2] += xc * xc * p.y;
    rhs[3] += xc * xc * xc * p.y;
  }

  // Normal equations matrix (4x4)
  // Row k: sum(x^(k+j)) for j=0..3 = rhs[k], where k goes from 3 down to 0
  const A = [
    [sums[6], sums[5], sums[4], sums[3]],
    [sums[5], sums[4], sums[3], sums[2]],
    [sums[4], sums[3], sums[2], sums[1]],
    [sums[3], sums[2], sums[1], sums[0]],
  ];
  const b = [rhs[3], rhs[2], rhs[1], rhs[0]];

  const sol = solveLinearSystem(A, b);
  if (!sol) {
    return null;
  }

  const [ac, bc, cc, dc] = sol;

  // Convert centered coefficients back to original x
  // y = ac*(x-m)³ + bc*(x-m)² + cc*(x-m) + dc
  const m = xMean;
  const a = ac;
  const bCoeff = bc - 3 * ac * m;
  const c = cc + 3 * ac * m * m - 2 * bc * m;
  const d = dc - ac * m * m * m + bc * m * m - cc * m;

  let ssTot = 0,
    ssRes = 0;
  for (const p of points) {
    const x = p.x;
    const predicted = a * x * x * x + bCoeff * x * x + c * x + d;
    ssTot += (p.y - yMean) ** 2;
    ssRes += (p.y - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    a: roundTo(a, 6),
    b: roundTo(bCoeff, 6),
    c: roundTo(c, 6),
    d: roundTo(d, 6),
    rSquared: roundTo(rSquared, 6),
  };
}

export function predictCubicY(reg: CubicRegressionResult, x: number): number {
  return reg.a * x * x * x + reg.b * x * x + reg.c * x + reg.d;
}

// ---------------------------------------------------------------------------
// Exponential regression (y = a·e^(bx))
// ---------------------------------------------------------------------------

export interface ExponentialRegressionResult {
  readonly a: number;
  readonly b: number;
  readonly rSquared: number;
}

/**
 * Compute exponential regression y = a·e^(bx) by linearising: ln(y) = ln(a) + bx.
 * Requires all y values to be positive.
 */
export function computeExponentialRegression(points: ScatterPlotPoint[]): ExponentialRegressionResult | null {
  const n = points.length;
  if (n < 2) {
    return null;
  }

  // Build log-transformed points in a single pass, skipping non-positive y
  const logPoints: ScatterPlotPoint[] = [];
  for (const p of points) {
    if (p.y > 0) {
      logPoints.push({ x: p.x, y: Math.log(p.y) });
    }
  }
  if (logPoints.length < 2) {
    return null;
  }

  const linReg = computeRegression(logPoints);
  if (!linReg) {
    return null;
  }

  const a = safeExp(linReg.b);
  const b = linReg.a;

  // R² on original scale
  let ySum = 0;
  for (const p of logPoints) {
    ySum += Math.exp(p.y); // recover original y
  }
  const yMean = ySum / logPoints.length;
  let ssTot = 0,
    ssRes = 0;
  for (const p of logPoints) {
    const origY = Math.exp(p.y);
    const predicted = a * safeExp(b * p.x);
    ssTot += (origY - yMean) ** 2;
    ssRes += (origY - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    a: roundTo(a, 6),
    b: roundTo(b, 6),
    rSquared: roundTo(rSquared, 6),
  };
}

export function predictExponentialY(reg: ExponentialRegressionResult, x: number): number {
  return reg.a * safeExp(reg.b * x);
}

// ---------------------------------------------------------------------------
// Power regression (y = a·x^b)
// ---------------------------------------------------------------------------

export interface PowerRegressionResult {
  readonly a: number;
  readonly b: number;
  readonly rSquared: number;
}

/**
 * Compute power regression y = a·x^b by linearising: ln(y) = ln(a) + b·ln(x).
 * Requires all x and y values to be positive.
 */
export function computePowerRegression(points: ScatterPlotPoint[]): PowerRegressionResult | null {
  const n = points.length;
  if (n < 2) {
    return null;
  }

  // Build log-transformed points in a single pass, skipping non-positive x or y
  const logPoints: ScatterPlotPoint[] = [];
  let ySum = 0;
  for (const p of points) {
    if (p.x > 0 && p.y > 0) {
      logPoints.push({ x: Math.log(p.x), y: Math.log(p.y) });
      ySum += p.y;
    }
  }
  if (logPoints.length < 2) {
    return null;
  }

  const linReg = computeRegression(logPoints);
  if (!linReg) {
    return null;
  }

  const a = safeExp(linReg.b);
  const b = linReg.a;

  // R² on original scale
  const yMean = ySum / logPoints.length;
  let ssTot = 0,
    ssRes = 0;
  for (const p of logPoints) {
    const origX = Math.exp(p.x);
    const origY = Math.exp(p.y);
    const predicted = a * Math.pow(origX, b);
    ssTot += (origY - yMean) ** 2;
    ssRes += (origY - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    a: roundTo(a, 6),
    b: roundTo(b, 6),
    rSquared: roundTo(rSquared, 6),
  };
}

export function predictPowerY(reg: PowerRegressionResult, x: number): number {
  if (x <= 0) {
    return 0;
  }
  return reg.a * Math.pow(x, reg.b);
}

// ---------------------------------------------------------------------------
// Logarithmic regression (y = a + b·ln(x))
// ---------------------------------------------------------------------------

export interface LogarithmicRegressionResult {
  readonly a: number;
  readonly b: number;
  readonly rSquared: number;
}

/**
 * Compute logarithmic regression y = a + b·ln(x).
 * Substitutes u = ln(x), then does linear regression y = a + bu.
 * Requires all x values to be positive.
 */
export function computeLogarithmicRegression(points: ScatterPlotPoint[]): LogarithmicRegressionResult | null {
  const n = points.length;
  if (n < 2) {
    return null;
  }

  // Build log-transformed x values in a single pass, skipping non-positive x
  const logPoints: ScatterPlotPoint[] = [];
  for (const p of points) {
    if (p.x > 0) {
      logPoints.push({ x: Math.log(p.x), y: p.y });
    }
  }
  if (logPoints.length < 2) {
    return null;
  }

  const linReg = computeRegression(logPoints);
  if (!linReg) {
    return null;
  }

  const a = linReg.b;
  const b = linReg.a;

  // R² on original scale (logPoints.y is already the original y)
  let ySum = 0;
  for (const p of logPoints) {
    ySum += p.y;
  }
  const yMean = ySum / logPoints.length;
  let ssTot = 0,
    ssRes = 0;
  for (const p of logPoints) {
    const predicted = a + b * p.x; // p.x is already ln(origX)
    ssTot += (p.y - yMean) ** 2;
    ssRes += (p.y - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    a: roundTo(a, 6),
    b: roundTo(b, 6),
    rSquared: roundTo(rSquared, 6),
  };
}

export function predictLogarithmicY(reg: LogarithmicRegressionResult, x: number): number {
  if (x <= 0) {
    return 0;
  }
  return reg.a + reg.b * Math.log(x);
}

// ---------------------------------------------------------------------------
// Logistic regression (y = L / (1 + Ce^(-kx)))
// ---------------------------------------------------------------------------

export interface LogisticRegressionResult {
  /** Carrying capacity (upper asymptote) */
  readonly L: number;
  /** Growth rate */
  readonly k: number;
  /** Shift parameter */
  readonly C: number;
  readonly rSquared: number;
}

/**
 * Compute logistic regression y = L / (1 + C·e^(-kx)) using gradient descent.
 * L is estimated from the data, then refined iteratively.
 */
export function computeLogisticRegression(points: ScatterPlotPoint[]): LogisticRegressionResult | null {
  const n = points.length;
  if (n < 3) {
    return null;
  }

  const sorted = [...points].sort((a, b) => a.x - b.x);

  let yMin = sorted[0].y;
  let yMax = sorted[0].y;
  let ySum = 0;
  for (const p of sorted) {
    if (p.y < yMin) {
      yMin = p.y;
    }
    if (p.y > yMax) {
      yMax = p.y;
    }
    ySum += p.y;
  }
  const yMean = ySum / n;

  // Initial estimates
  let L = yMax * 1.1;
  let k = 1;
  let C = (L - sorted[0].y) / Math.max(sorted[0].y, 0.01);

  // Estimate k from midpoint slope
  const xRange = sorted[n - 1].x - sorted[0].x;
  const midIdx = Math.floor(n / 2);
  if (midIdx > 0 && midIdx < n - 1) {
    const dyDx = (sorted[midIdx + 1].y - sorted[midIdx - 1].y) / (sorted[midIdx + 1].x - sorted[midIdx - 1].x);
    const midY = sorted[midIdx].y;
    if (midY > 0 && midY < L) {
      k = Math.abs((4 * dyDx) / L);
    }
  }
  if (!isFinite(k) || k <= 0) {
    k = 1 / Math.max(xRange, 1);
  }
  if (!isFinite(C) || C <= 0) {
    C = 1;
  }

  // Gradient descent
  const xScale = Math.max(xRange, 1);
  const baseLr = 0.0001;
  for (let iter = 0; iter < 1000; iter++) {
    let dL = 0,
      dk = 0,
      dC = 0;
    for (const p of sorted) {
      const expTerm = safeExp(-k * p.x);
      const denom = 1 + C * expTerm;
      const predicted = L / denom;
      const residual = predicted - p.y;

      dL += residual / denom;
      dk += (residual * (L * C * p.x * expTerm)) / (denom * denom);
      dC += (residual * (-L * expTerm)) / (denom * denom);
    }

    const newL = L - baseLr * L * (2 / n) * dL;
    const newK = k - (baseLr / xScale) * (2 / n) * dk;
    const newC = C - baseLr * C * (2 / n) * dC;

    // Guard against NaN from overflow — keep previous values if update is invalid
    L = isFinite(newL) && newL > 0 ? newL : L;
    k = isFinite(newK) && newK > 0 ? newK : k;
    C = isFinite(newC) && newC > 0 ? newC : C;
  }

  let ssTot = 0,
    ssRes = 0;
  for (const p of sorted) {
    const predicted = L / (1 + C * safeExp(-k * p.x));
    ssTot += (p.y - yMean) ** 2;
    ssRes += (p.y - predicted) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    L: roundTo(L, 4),
    k: roundTo(k, 4),
    C: roundTo(C, 4),
    rSquared: roundTo(rSquared, 6),
  };
}

export function predictLogisticY(reg: LogisticRegressionResult, x: number): number {
  return reg.L / (1 + reg.C * safeExp(-reg.k * x));
}

/**
 * Generate smooth curve points for a non-linear regression.
 * Returns an array of [x, y] pairs suitable for ECharts line series.
 * Non-finite values are filtered out to prevent broken chart rendering.
 */
export function generateCurvePoints(
  fn: (x: number) => number,
  xMin: number,
  xMax: number,
  numPoints = 200,
): Array<[number, number]> {
  const step = (xMax - xMin) / numPoints;
  const data: Array<[number, number]> = [];
  for (let i = 0; i <= numPoints; i++) {
    const x = xMin + i * step;
    const y = fn(x);
    if (isFinite(y)) {
      data.push([x, y]);
    }
  }
  return data;
}
